import React, { type Dispatch, type FC, type SetStateAction } from "react";
import type { TFunction } from "i18next";
import {
  Download, ExternalLink,
} from "lucide-react";
import {
  DefinitionTooltip,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableSearch,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  TablePagePagination,
  toggleTableSort,
  dashboardDataTable as dt,
  type SortDirection,
} from "src/components/common";
import {
  formatOrganicPostTypeLabel,
  getOrganicContentSectionDefinitionInfo,
  getOrganicPlatformMetricDefinitionInfo,
} from "../OrganicMediaDefinitions";
import PostMediaThumb from "../PostMediaThumb";
import { formatNumber } from "src/utils/numberFormat";
import {
  withBidiIsolate,
  type PostTableSortKey,
} from "./FacebookContentHelpers";
import "../../../styles/FacebookContentReport/FacebookContentPostTable.scss";

export type FacebookPostTableRow = {
  postSystemId?: string;
  caption: string;
  postType: string;
  thumbnailUrl?: string;
  impressions: number;
  impressionsFormattedValue?: string;
  clicks: number;
  clicksFormattedValue?: string;
  loves: number;
  lovesFormattedValue?: string;
  comments: number;
  commentsFormattedValue?: string;
  engagementRate: number;
  engagementRateFormattedValue?: string;
  postUrl?: string | null;
};

export type FacebookContentPostTableProps = {
  t: TFunction;
  i18n: { language: string };
  sortedPostDetails: FacebookPostTableRow[];
  postTableSearch: string;
  setPostTableSearch: Dispatch<SetStateAction<string>>;
  handleExportPosts: () => void;
  setPostTableSort: Dispatch<
    SetStateAction<{ key: PostTableSortKey; direction: SortDirection } | null>
  >;
  getCaptionPreview: (text: string) => string;
  truncateCaption: (text: string, maxLen?: number) => string;
  currentPostPage: number;
  setCurrentPostPage: Dispatch<SetStateAction<number>>;
  totalPostPages: number;
  exportPostDetails: FacebookPostTableRow[];
};

const FacebookContentPostTable: FC<{ props: FacebookContentPostTableProps }> = ({ props }) => {
  const {
    t,
    i18n,
    sortedPostDetails,
    postTableSearch,
    setPostTableSearch,
    handleExportPosts,
    setPostTableSort,
    getCaptionPreview,
    truncateCaption,
    currentPostPage,
    setCurrentPostPage,
    totalPostPages,
    exportPostDetails,
  } = props;

  return (
    <div
      className="organic-content-report__post-table-block"
      data-pdf-block="post-table"
      data-pdf-splittable
    >
      <div className="organic-post-details organic-post-details--modern">
        <DashboardDataTablePanel>
        <DashboardDataTablePanelHeader>
          <div className="organic-content-panel__title">
            <h3>{t("organicMedia.facebook.sections.postLevelPerformance")}</h3>
            <DefinitionTooltip
              label={t("organicMedia.facebook.sections.postLevelPerformance")}
              info={getOrganicContentSectionDefinitionInfo("postLevelPerformance", t)}
              formulaTitle={t("organicMedia.common.formula")}
            />
          </div>
          <div className="organic-post-details__table-toolbar">
            <DashboardDataTableSearch
              value={postTableSearch}
              onChange={event => setPostTableSearch(event.target.value)}
              placeholder={t("organicMedia.contentReport.searchPlaceholder")}
            />
            <button
              type="button"
              className="organic-post-details__export-button"
              onClick={handleExportPosts}
              disabled={sortedPostDetails.length === 0}
            >
              <Download size={14} aria-hidden />
              {t("organicMedia.contentReport.exportCsv")}
            </button>
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="organic-post-details__table-wrap">
          <DashboardDataTableTable className="organic-post-details__table organic-post-details__table--modern">
            <colgroup>
              <col className="organic-post-details__col-post" />
              <col className="organic-post-details__col-metric" />
              <col className="organic-post-details__col-metric" />
              <col className="organic-post-details__col-metric" />
              <col className="organic-post-details__col-metric" />
              <col className="organic-post-details__col-metric" />
              <col className="organic-post-details__col-action" />
            </colgroup>
            <thead>
              <tr>
                <th className="organic-post-details__th-post">
                  <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "caption"))}>
                    {t("organicMedia.facebook.table.post")?.toUpperCase()}
                  </DashboardDataTableSortButton>
                </th>
                <th className="organic-post-details__th-tooltip">
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "impressions"))}>
                      {t("organicMedia.facebook.table.impressions")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t("organicMedia.facebook.table.impressions")}
                      info={getOrganicPlatformMetricDefinitionInfo("facebook", "impressions", t("organicMedia.facebook.table.impressions"), t)}
                      formulaTitle={t("organicMedia.common.formula")}
                    />
                  </span>
                </th>
                <th className="organic-post-details__th-tooltip">
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "clicks"))}>
                      {t("organicMedia.facebook.table.clicks")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t("organicMedia.facebook.table.clicks")}
                      info={getOrganicPlatformMetricDefinitionInfo("facebook", "linkClicks", t("organicMedia.facebook.table.clicks"), t)}
                      formulaTitle={t("organicMedia.common.formula")}
                    />
                  </span>
                </th>
                <th className="organic-post-details__th-tooltip">
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "loves"))}>
                      {t("organicMedia.facebook.table.likes")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t("organicMedia.facebook.table.likes")}
                      info={getOrganicPlatformMetricDefinitionInfo("facebook", "likes", t("organicMedia.facebook.table.likes"), t)}
                      formulaTitle={t("organicMedia.common.formula")}
                    />
                  </span>
                </th>
                <th className="organic-post-details__th-tooltip">
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "comments"))}>
                      {t("organicMedia.facebook.table.comments")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t("organicMedia.facebook.table.comments")}
                      info={getOrganicPlatformMetricDefinitionInfo("facebook", "comments", t("organicMedia.facebook.table.comments"), t)}
                      formulaTitle={t("organicMedia.common.formula")}
                    />
                  </span>
                </th>
                <th className="organic-post-details__th-tooltip">
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "engagementRate"))}>
                      {t("organicMedia.facebook.table.engRate")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t("organicMedia.facebook.table.engRate")}
                      info={getOrganicPlatformMetricDefinitionInfo("facebook", "engagementRate", t("organicMedia.facebook.table.engRate"), t)}
                      align="end"
                      formulaTitle={t("organicMedia.common.formula")}
                    />
                  </span>
                </th>
                <th>{t("organicMedia.facebook.table.link")?.toUpperCase()}</th>
              </tr>
            </thead>
            <tbody>
              {exportPostDetails.map((row, index) => (
                <tr key={`${row.postSystemId || row.caption}-${index}`}>
                  <td>
                    <div className="organic-post-details__post-cell">
                      <div className="organic-post-details__thumb" aria-hidden>
                        <PostMediaThumb thumbnailUrl={row.thumbnailUrl} platform="facebook" />
                      </div>
                      <div className="organic-post-details__post-copy">
                        <div className="organic-post-details__caption-wrap">
                          <strong>{truncateCaption(row.caption, 48)}</strong>
                          <div className="organic-post-details__caption-tooltip">{getCaptionPreview(row.caption)}</div>
                        </div>
                        <div className="organic-post-details__tag-row">
                          <span className="organic-post-details__mini-tag organic-post-details__mini-tag--platform">
                            {t("organicMedia.platform.facebook")}
                          </span>
                          <span className="organic-post-details__mini-tag organic-post-details__mini-tag--type">
                            {formatOrganicPostTypeLabel(row.postType, t)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{row.impressionsFormattedValue ?? formatNumber(row.impressions, {}, i18n.language)}</td>
                  <td>{row.clicksFormattedValue ?? formatNumber(row.clicks, {}, i18n.language)}</td>
                  <td>{row.lovesFormattedValue ?? formatNumber(row.loves, {}, i18n.language)}</td>
                  <td>{row.commentsFormattedValue ?? formatNumber(row.comments, {}, i18n.language)}</td>
                  <td>
                    {row.engagementRateFormattedValue ??
                      withBidiIsolate(
                        `${formatNumber(row.engagementRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                        i18n.language
                      )}
                  </td>
                  <td className="organic-post-details__action-cell">
                    {row.postUrl ? (
                      <a
                        className="organic-post-details__action-button"
                        href={row.postUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink size={14} aria-hidden />
                        {t("organicMedia.facebook.table.viewPost")}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="organic-post-details__action-button"
                        disabled
                        title={t("organicMedia.contentReport.postLinkUnavailable")}
                      >
                        <ExternalLink size={14} aria-hidden />
                        {t("organicMedia.facebook.table.viewPost")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={currentPostPage}
          totalPages={totalPostPages}
          onPrevious={() => setCurrentPostPage(page => Math.max(1, page - 1))}
          onNext={() => setCurrentPostPage(page => Math.min(totalPostPages, page + 1))}
          className="organic-post-details__pager"
        />
        </DashboardDataTablePanel>
      </div>
    </div>
  );
};

export default FacebookContentPostTable;
